/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * This is the entry point for Feature Hub App integration
 */
import React from 'react';
import type { FeatureServices, FeatureAppDefinition } from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { IAuthServiceProviderV1 } from '@oneaudi/audi-auth-service';

import { FootnoteContextProvider, InViewContextProvider } from '@oneaudi/feature-app-utils';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type {
  AudiFootnoteReferenceServiceInterfaceV3,
  AudiFootnoteRefernceServiceScopeManagerInterfaceV3,
} from '@oneaudi/footnote-reference-service';
import type { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service';
import type { EnvConfigServiceV1 } from '@volkswagen-onehub/audi-env-config-service/dist/v1/EnvConfigServiceV1';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
// eslint-disable-next-line
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';

import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import FeatureApp from './FeatureApp';
import { ContextProvider } from './context';
import { getDesignLibrary } from '../alpha-helper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'vw:authService'?: IAuthServiceProviderV1;
  readonly 'audi-content-service'?: ContentServiceV1;
  readonly 'dbad:audi-vue-formatter-service'?: VueFormatterServiceInterfaceV1;
  readonly 'audi-footnote-reference-service'?: AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
}

// allow setting ssr mode via URL, so it's set before anything launches
if (typeof window !== 'undefined') {
  const searchParams = new URLSearchParams(window.location.search);
  let ssr = false;
  if (searchParams.has('ssr')) {
    const ssrQueryParam = searchParams.get('ssr') as string;
    ssr = ssrQueryParam === 'true';

    console.debug(`Query parameter 'ssr' recognized: ${ssr}`);
  }

  (window as any).__SSR__ = ssr;
}

export type SSRContent = any | undefined;

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'audi:envConfigService': '^1.0.0',
      's2:logger': '^1.0.0',
      'locale-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'dbad:audi-vue-formatter-service': '^1.0.0',
    },
    externals: {
      react: '^16.13.0 || ^17.0.2 || ^18.x.x',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
    },
  },

  create: ({ featureServices, featureAppId }) => {
    const envConfigService = featureServices['audi:envConfigService'] as EnvConfigServiceV1;
    const loggerService = featureServices['s2:logger'];
    const contentService = featureServices['audi-content-service'];
    const localeService = featureServices['locale-service'];
    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];

    const providerfootnoteService = featureServices[
      'audi-footnote-reference-service'
    ] as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;

    const footnoteReferenceService: AudiFootnoteReferenceServiceInterfaceV3 =
      providerfootnoteService?.getDefaultScopeRefService();

    // SSR
    // --------------------------------------------------------------------
    const asyncSSRManager = featureServices['s2:async-ssr-manager'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    // on the server
    if (asyncSSRManager) {
      asyncSSRManager.scheduleRerender(
        (async () => {
          const content = await contentService?.getContent();
          serializedStateManager?.register(() => JSON.stringify(content));
        })(),
      );
    }

    return {
      render: () => {
        return (
          <InViewContextProvider logger={loggerService}>
            <UniversalEditorProvider
              contentService={featureServices['audi-content-service']!}
              renderModeService={featureServices['audi-render-mode-service']}
            >
              <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                <ContextProvider
                  featureAppId={featureAppId}
                  localeService={localeService}
                  vueFormatterService={vueFormatterService!}
                  contentService={contentService!}
                  footnoteReferenceService={providerfootnoteService}
                  envConfigService={envConfigService}
                  loggerService={loggerService}
                >
                  <div data-fefa-custom-id={footnoteReferenceService?.getConsumerId()}>
                    <FeatureApp
                      trackingService={featureServices['audi-tracking-service']}
                      loggerService={loggerService}
                      localeService={localeService}
                    />
                  </div>
                </ContextProvider>
              </FootnoteContextProvider>
            </UniversalEditorProvider>
          </InViewContextProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
